import { useCallback, useState } from "react"
import type {
  DealDrawer as DealDrawerData,
  LinkedDealPipelineStage,
} from "@/types/v2/conversation"
import { getCsrfToken } from "@/lib/csrf"
import {
  replaceDealProp,
  type DealPropKey,
} from "@/lib/deal-prop-update"

// Submission logic for moving a deal between stages.
// Hits the existing v2 endpoint that the kanban
// drag-and-drop uses, updates the deal prop optimistically
// via `router.replaceProp`, and reverts on rejection.
// Used by both the desktop StagePicker popover and the
// mobile bottom-sheet picker — they differ only in
// presentation.

export type { DealPropKey }

interface UseStageMoveOptions {
  deal: DealDrawerData
  // Which prop on the host page holds the deal payload.
  // `deal` on the show page; `dealDrawer` on the deals
  // index where the slideout is hosted.
  dealPropKey: DealPropKey
  // Called after a successful PATCH so the host can close
  // the picker. Not called on no-op (current stage) or
  // on failure — failure leaves the picker open so the
  // user sees the revert and can retry.
  onSuccess?: () => void
}

interface UseStageMoveResult {
  submitting: boolean
  moveToStage: (stage: LinkedDealPipelineStage) => void
}

export function useStageMove({
  deal,
  dealPropKey,
  onSuccess,
}: UseStageMoveOptions): UseStageMoveResult {
  // In-flight guard — a second tap while a PATCH is
  // pending is ignored. The optimistic update plus
  // realtime reconcile mean the user can re-open the
  // picker after the first move completes (success or
  // fail) and pick a different stage.
  const [submitting, setSubmitting] = useState(false)

  const moveToStage = useCallback(
    (stage: LinkedDealPipelineStage) => {
      if (submitting) return
      if (stage.id === deal.stage.id) {
        onSuccess?.()
        return
      }

      const previousDeal = deal
      const optimisticDeal: DealDrawerData = {
        ...deal,
        stage: {
          id: stage.id,
          name: stage.name,
          stageType: stage.stageType,
          position: stage.position,
        },
        pipeline: {
          ...deal.pipeline,
          stages: deal.pipeline.stages.map((s) => ({
            ...s,
            isCurrent: s.id === stage.id,
            // `isPassed` is "this stage is earlier than the
            // current one AND the deal hasn't fallen back to
            // an earlier stage." Match the server contract:
            // strictly less-than-position with respect to the
            // newly selected stage.
            isPassed: s.position < stage.position,
          })),
        },
      }

      replaceDealProp(dealPropKey, () => optimisticDeal)

      setSubmitting(true)
      fetch(`/v2/deals/stages/${stage.id}/cards/${deal.id}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`)
          }
          // Success — the host page's ActionCable
          // subscription will receive a `deal.updated`
          // shortly and reconcile any details we
          // approximated (e.g. activity feed). Close
          // and let it land.
          onSuccess?.()
        })
        .catch(() => {
          // Server rejected — undo the optimistic
          // update. We don't auto-close so the user
          // sees that the move didn't take and can
          // retry.
          replaceDealProp(dealPropKey, () => previousDeal)
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
    [deal, dealPropKey, onSuccess, submitting],
  )

  return { submitting, moveToStage }
}
